import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TASK_STATUSES, TASK_PRIORITIES } from '../utils/constants';

const WAITING_TYPES = ['En espera de cliente', 'En espera de usuario/Presidencia'];
import { getCurrentWeekRange, formatDate } from '../utils/dateHelpers';

const AdvancedFilters = ({ isOpen, onClose, onApply, areas = [], departments = [], users = [] }) => {
  const [filters, setFilters] = useState({
    status: [],
    priority: [],
    department_id: [],
    area_id: [],
    responsible_user_id: [],
    waiting_type: [],
    has_attachments: null,
    overdue_only: false,
    due_this_week: false,
    weekStart: '',
    weekEnd: ''
  });

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    setFilters({
      status: [],
      priority: [],
      department_id: [],
      area_id: [],
      responsible_user_id: [],
      waiting_type: [],
      has_attachments: null,
      overdue_only: false,
      due_this_week: false,
      weekStart: '',
      weekEnd: ''
    });
  };

  const toggleArrayFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value]
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="decko-card w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-foreground">Filtros Avanzados</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Estados */}
            <div>
              <label className="block text-sm font-bold text-foreground mb-3">Estado</label>
              <div className="space-y-2">
                {TASK_STATUSES.map(status => (
                  <label key={status} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                    <input
                      type="checkbox"
                      checked={filters.status.includes(status)}
                      onChange={() => toggleArrayFilter('status', status)}
                      className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Prioridades */}
            <div>
              <label className="block text-sm font-bold text-foreground mb-3">Prioridad</label>
              <div className="space-y-2">
                {TASK_PRIORITIES.map(priority => (
                  <label key={priority} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                    <input
                      type="checkbox"
                      checked={filters.priority.includes(priority)}
                      onChange={() => toggleArrayFilter('priority', priority)}
                      className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">{priority}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Departamentos */}
            <div>
              <label className="block text-sm font-bold text-foreground mb-3">Departamento</label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {departments.map(dept => (
                  <label key={dept.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                    <input
                      type="checkbox"
                      checked={filters.department_id.includes(dept.id)}
                      onChange={() => toggleArrayFilter('department_id', dept.id)}
                      className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">{dept.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Responsables */}
            <div>
              <label className="block text-sm font-bold text-foreground mb-3">Responsable</label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {users.map(usr => (
                  <label key={usr.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                    <input
                      type="checkbox"
                      checked={filters.responsible_user_id.includes(usr.id)}
                      onChange={() => toggleArrayFilter('responsible_user_id', usr.id)}
                      className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">{usr.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tipo de Espera */}
            <div>
              <label className="block text-sm font-bold text-foreground mb-3">En Espera De</label>
              <div className="space-y-2">
                {WAITING_TYPES.map(type => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                    <input
                      type="checkbox"
                      checked={filters.waiting_type.includes(type)}
                      onChange={() => toggleArrayFilter('waiting_type', type)}
                      className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Opciones adicionales */}
            <div>
              <label className="block text-sm font-bold text-foreground mb-3">Opciones</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                  <input
                    type="checkbox"
                    checked={filters.overdue_only}
                    onChange={(e) => setFilters(prev => ({ ...prev, overdue_only: e.target.checked }))}
                    className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Solo Vencidas</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                  <input
                    type="checkbox"
                    checked={filters.due_this_week}
                    onChange={(e) => setFilters(prev => ({ ...prev, due_this_week: e.target.checked }))}
                    className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Vencen Esta Semana</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-secondary">
                  <input
                    type="checkbox"
                    checked={filters.has_attachments === true}
                    onChange={(e) => setFilters(prev => ({ ...prev, has_attachments: e.target.checked ? true : null }))}
                    className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Con Adjuntos</span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-border">
            <button
              onClick={handleReset}
              className="btn-secondary flex-1"
            >
              Limpiar Todo
            </button>
            <button
              onClick={handleApply}
              className="btn-primary flex-1"
            >
              Aplicar Filtros
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdvancedFilters;
